import {
  personsInEntityKeys,
  queryClient,
  usePersonsInEntity,
} from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { EmptyStateV2 } from '../../components/empty-state/EmptyStateV2';
import { KeyPersonsTable } from './KeyPersonsTable';
import { AddKeyPersonModal } from './Modals/AddKeyPersonModal';
import { DeleteKeyPersonModal } from './Modals/DeleteKeyPersonModal';

const KeyPersons = ({
  companyId,
  businessEntityId,
  isEditable,
  hideTitle = false,
  shouldShowViewKeyPersonButton = true,
  className,
  disabled = false,
  banner,
  onViewKeyPerson,
  allowNameEditInEditMode = false,
}: {
  companyId: number;
  businessEntityId: number;
  isEditable: boolean;
  hideTitle?: boolean;
  shouldShowViewKeyPersonButton?: boolean;
  className?: string;
  disabled?: boolean;
  banner?: React.ReactNode;
  onViewKeyPerson?: (keyPersonId: number) => void;
  allowNameEditInEditMode?: boolean;
}) => {
  const deleteKeyPersonModalVisibility = useToggle();
  const addKeyPersonModalVisibility = useToggle();
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [personIdForDeletion, setPersonIdForDeletion] = useState<number | null>(
    null
  );

  const { data: personsInEntity, error: personsInEntityError } =
    usePersonsInEntity(businessEntityId);

  const router = useRouter();

  return (
    <div
      className={cn(
        'flex-1 h-full overflow-y-auto  flex flex-col gap-4xl',
        className
      )}
    >
      <AddKeyPersonModal
        companyId={companyId}
        isOpen={addKeyPersonModalVisibility.visible}
        onClose={addKeyPersonModalVisibility.toggle}
        onSuccess={() => {
          setSelectedPersonId(null);
          queryClient.invalidateQueries({
            queryKey: [
              personsInEntityKeys.FETCH_PERSONS_IN_ENTITY,
              { businessEntityId },
            ],
          });
        }}
        businessEntityId={businessEntityId}
        alreadyExistingKeyPersonIds={personsInEntity?.persons?.map(
          (person) => person.id
        )}
        personId={selectedPersonId}
        isEditMode={!!selectedPersonId}
        allowNameEditInEditMode={allowNameEditInEditMode}
      />
      <DeleteKeyPersonModal
        isOpen={deleteKeyPersonModalVisibility.visible}
        onClose={() => {
          deleteKeyPersonModalVisibility.toggle();
          setPersonIdForDeletion(null);
        }}
        personId={personIdForDeletion}
        businessEntityId={businessEntityId}
        onSuccess={() => {
          setPersonIdForDeletion(null);
        }}
      />
      <div className="w-full flex justify-between gap-4xl">
        {!hideTitle && (
          <div className="flex flex-col gap-xs">
            <h3 className="display-xs-bold">Key Persons</h3>
            <p className="txt-small-regular text-text-tertiary">
              View and manage the individuals and entities who play a vital role
              in your company
            </p>
          </div>
        )}
        {!disabled && isEditable ? (
          <Button
            size="sm"
            className="h-fit"
            onClick={() => {
              setSelectedPersonId(null);
              addKeyPersonModalVisibility.show();
            }}
          >
            <Icons.Plus className="size-4" />
            <span>Add key person</span>
          </Button>
        ) : undefined}
      </div>
      {banner}
      <div className="flex flex-col gap-4xl">
        {personsInEntity?.persons?.length === 0 ? (
          <EmptyStateV2
            title="No key persons found"
            description="There are no key persons in the entity."
          />
        ) : (
          <KeyPersonsTable
            companyId={companyId}
            disabled={disabled}
            keyPersons={personsInEntity?.persons || []}
            onAddKeyPerson={(keyPersonId) => {
              setSelectedPersonId(keyPersonId);
              addKeyPersonModalVisibility.show();
            }}
            onDeleteKeyPerson={(keyPersonId) => {
              setPersonIdForDeletion(keyPersonId);
              deleteKeyPersonModalVisibility.show();
            }}
            onViewKeyPerson={(keyPersonId) => {
              if (onViewKeyPerson) {
                onViewKeyPerson(keyPersonId);
              } else {
                router.push(
                  `/corporation/${businessEntityId}/key-persons/${keyPersonId}`
                );
              }
            }}
            isEditable={isEditable}
            shouldShowViewKeyPersonButton={shouldShowViewKeyPersonButton}
          />
        )}
      </div>
    </div>
  );
};

export { KeyPersons };
